import { isValidSchema } from "../util";
import { Frame as FrameV2 } from "../framer_v2/frame";
import { Campaign } from "../campaign";
import { FrameDataMeta } from "../framedatameta";
import { CaptureSystem } from "../capturesystem";
export class FrameV3 {
    constructor(uid, timestamp, type, sequence_uid, campaign_uid, campaign_name, campaign_type, route_uid, height_above_ground, direction, play_reverted, properties, 
    // position & orientation
    longitude, latitude, height, epsg_height, omega, phi, kappa, prec_longitude, prec_latitude, prec_height, prec_omega, prec_phi, prec_kappa, 
    // image & dispmap meta
    data_path, multi_head_meta, multi_head_mask, im_type, im_type_name, im_tile_size, im_width, im_height, im_focal_length, im_pixel_size, dm_reduction_factor, dm_basis, dm_num_disparities) {
        this.uid = uid;
        this.timestamp = timestamp;
        this.type = type == "flexcubemap" ? "cubemap" : type;
        this.sequence_uid = sequence_uid;
        this.campaign_uid = campaign_uid;
        this.campaign_name = campaign_name;
        this.campaign_type = campaign_type;
        this.route_uid = route_uid;
        this.height_above_ground = height_above_ground;
        this.direction = direction;
        this.play_reverted = play_reverted;
        this.properties = properties;
        // position & orientation
        this.longitude = longitude;
        this.latitude = latitude;
        this.height = height;
        this.epsg_height = epsg_height;
        this.omega = (omega / Math.PI) * 180;
        this.phi = (phi / Math.PI) * 180;
        this.kappa = (kappa / Math.PI) * 180;
        this.prec_longitude = prec_longitude / 1000;
        this.prec_latitude = prec_latitude / 1000;
        this.prec_height = prec_height / 1000;
        this.prec_omega = ((prec_omega / Math.PI) * 180) / 1000;
        this.prec_phi = ((prec_phi / Math.PI) * 180) / 1000;
        this.prec_kappa = ((prec_kappa / Math.PI) * 180) / 1000;
        // image & dispmap meta
        this.data_path = data_path;
        this.multi_head_meta = multi_head_meta;
        this.multi_head_mask = multi_head_mask;
        this.im_type = im_type;
        this.im_type_name = im_type_name;
        this.im_tile_size = im_tile_size;
        this.im_height = im_height;
        this.im_width = im_width;
        this.im_focal_length = im_focal_length;
        this.im_pixel_size = im_pixel_size * 1000;
        this.capture_system_id = `${im_type}/${direction}/${im_type_name}`;
        this.dm_reduction_factor = dm_reduction_factor;
        this.dm_num_disparities = dm_num_disparities;
        this.dm_basis = dm_basis;
        this.prec_disparities = 1;
        this.prec_dm_basis = 0.1;
        this.prec_im_focal_length = 0.001;
        for (const key in this.multi_head_meta) {
            const sensor = this.multi_head_meta[key];
            sensor.domega = (sensor.domega / Math.PI) * 180;
            sensor.dphi = (sensor.dphi / Math.PI) * 180;
            sensor.dkappa = (sensor.dkappa / Math.PI) * 180;
            this.multi_head_meta[key] = sensor;
        }
    }
    static create(payload) {
        return new FrameV3(payload.uid, payload.timestamp, payload.type, payload.sequence_uid, payload.campaign_uid, payload.campaign_name, payload.campaign_type, payload.route_uid, payload.height_above_ground, payload.direction, payload.play_reverted, payload.properties, 
        // position & orientation
        payload.longitude, payload.latitude, payload.height, payload.epsg_height, payload.omega, payload.phi, payload.kappa, payload.prec_longitude, payload.prec_latitude, payload.prec_height, payload.prec_omega, payload.prec_phi, payload.prec_kappa, 
        // image & dispmap meta
        payload.data_path, payload.multi_head_meta, payload.multi_head_mask, payload.im_type, payload.im_type_name, payload.im_tile_size, payload.im_width, payload.im_height, payload.im_focal_length, payload.im_pixel_size, payload.dm_reduction_factor, payload.dm_basis, payload.dm_num_disparities);
    }
    static _calculateMaxzoom(image_size, tile_size) {
        const quadsize = tile_size * 2 ** Math.ceil(Math.log2(Math.max(...image_size) / tile_size));
        const ntiles = Math.ceil(quadsize / tile_size);
        return Math.log2(ntiles);
    }
    static FrameV3toV2(frame_v3, collection) {
        const campaign = new Campaign(frame_v3.campaign_uid, undefined, frame_v3.campaign_name, new Date(frame_v3.timestamp));
        const imagemeta_structure_mapping = {
            stereo: "lr",
            mono: "l",
            calotte: "all",
            cubemap: "all",
            flexcubemap: "all",
        };
        const depthmapmeta_structure_mapping = {
            stereo: "l",
            mono: "l",
            calotte: "none",
            cubemap: "all",
            flexcubemap: "all",
        };
        const framedatameta = new FrameDataMeta(undefined, frame_v3.im_pixel_size, frame_v3.im_width, frame_v3.im_height, undefined, this._calculateMaxzoom([frame_v3.im_width, frame_v3.im_height], frame_v3.im_tile_size), "jpg", imagemeta_structure_mapping[frame_v3.type], frame_v3.im_focal_length, frame_v3.im_pixel_size * frame_v3.dm_reduction_factor, frame_v3.dm_num_disparities, frame_v3.im_width / frame_v3.dm_reduction_factor, frame_v3.im_height / frame_v3.dm_reduction_factor, undefined, this._calculateMaxzoom([frame_v3.im_width / frame_v3.dm_reduction_factor, frame_v3.im_height / frame_v3.dm_reduction_factor], frame_v3.im_tile_size), "png", depthmapmeta_structure_mapping[frame_v3.type], frame_v3.im_focal_length, frame_v3.im_tile_size, frame_v3.data_path.slice(5), frame_v3.dm_basis, frame_v3.prec_dm_basis, frame_v3.prec_disparities, frame_v3.prec_im_focal_length);
        const CS_TYPE_MAPPER = {
            rgb_image: 1,
            grey_image: 2,
            rgb_panorama: 3,
            pointcloud_panorama: 4,
            lra: 5,
            other: 6,
        };
        const _sensordefinition_cubemap = [
            {
                focallength: 8,
                pos: "0",
                bx: 0,
                by2: 0,
                bz: 0,
                domega: 0,
                dphi: 0,
                dkappa: 0,
                mosaic: {},
            },
            {
                focallength: 8,
                pos: "1",
                bx: 0,
                by2: 0,
                bz: 0,
                domega: 0,
                dphi: 180,
                dkappa: 0,
                mosaic: {},
            },
            {
                focallength: 8,
                pos: "2",
                bx: 0,
                by2: 0,
                bz: 0,
                domega: 0,
                dphi: 90,
                dkappa: 0,
                mosaic: {},
            },
            {
                focallength: 8,
                pos: "3",
                bx: 0,
                by2: 0,
                bz: 0,
                domega: 0,
                dphi: 270,
                dkappa: 0,
                mosaic: {},
            },
            {
                focallength: 8,
                pos: "4",
                bx: 0,
                by2: 0,
                bz: 0,
                domega: 90,
                dphi: 0,
                dkappa: 0,
                mosaic: {},
            },
            {
                focallength: 8,
                pos: "5",
                bx: 0,
                by2: 0,
                bz: 0,
                domega: 270,
                dphi: 0,
                dkappa: 0,
                mosaic: {},
            },
        ];
        let sensors_internal = [];
        for (const key in frame_v3.multi_head_meta) {
            const sensor = frame_v3.multi_head_meta[key];
            let mask = {};
            if (frame_v3.multi_head_mask) {
                mask = frame_v3.multi_head_mask[key];
            }
            const sensor_internal = {
                focallength: sensor.focal_length,
                pos: key,
                bx: sensor.dx,
                by2: sensor.dy,
                bz: sensor.dz,
                domega: sensor.domega,
                dphi: sensor.dphi,
                dkappa: sensor.dkappa,
                mosaic: mask,
            };
            sensors_internal.push(sensor_internal);
        }
        if (!frame_v3.multi_head_meta && frame_v3.type == "cubemap")
            sensors_internal = _sensordefinition_cubemap;
        if (!frame_v3.multi_head_meta && frame_v3.type == "mono") {
            sensors_internal = [
                {
                    bx: 0,
                    by2: 0,
                    bz: 0,
                    domega: 0,
                    dphi: 0,
                    dkappa: 0,
                    focallength: frame_v3.im_focal_length,
                    pos: "0",
                    mosaic: {
                        longmin: undefined,
                        latmin: undefined,
                        longmax: undefined,
                        latmax: undefined,
                    },
                },
            ];
        }
        if (!frame_v3.multi_head_meta && frame_v3.type == "stereo") {
            sensors_internal = [
                {
                    bx: 0,
                    by2: 0,
                    bz: 0,
                    domega: 0,
                    dphi: 0,
                    dkappa: 0,
                    focallength: frame_v3.im_focal_length,
                    pos: "0",
                    mosaic: {
                        longmin: undefined,
                        latmin: undefined,
                        longmax: undefined,
                        latmax: undefined,
                    },
                },
                {
                    bx: frame_v3.dm_basis,
                    by2: 0,
                    bz: 0,
                    domega: 0,
                    dphi: 0,
                    dkappa: 0,
                    focallength: frame_v3.im_focal_length,
                    pos: "1",
                    mosaic: {
                        longmin: undefined,
                        latmin: undefined,
                        longmax: undefined,
                        latmax: undefined,
                    },
                },
            ];
        }
        let projection;
        if (frame_v3.type == "mono" || frame_v3.type == "stereo") {
            projection = "perspective";
        }
        else if (frame_v3.type == "cubemap" || frame_v3.type == "calotte") {
            projection = "panoramic";
        }
        const capturesystem = new CaptureSystem(frame_v3.capture_system_id, undefined, undefined, frame_v3.im_type_name, 0, projection, frame_v3.height_above_ground, frame_v3.direction, CS_TYPE_MAPPER[frame_v3.im_type], sensors_internal);
        const framev2 = new FrameV2(frame_v3.uid, frame_v3.uid, collection, frame_v3.sequence_uid, frame_v3.type, frame_v3.sequence_uid, undefined, // Framenumber can be accessed with geoframe.getFramenumber()
        projection, frame_v3.timestamp, undefined, frame_v3.play_reverted == true ? "backward" : "forward", frame_v3.longitude, frame_v3.latitude, frame_v3.height, frame_v3.omega, frame_v3.phi, frame_v3.kappa, frame_v3.prec_longitude, frame_v3.prec_latitude, frame_v3.prec_height, frame_v3.prec_omega, frame_v3.prec_phi, frame_v3.prec_kappa, frame_v3.capture_system_id, "framedatametaID", frame_v3.campaign_uid, capturesystem, framedatameta, campaign, 4326, // WGS84
        frame_v3.epsg_height, // Ellipsoidal,
        frame_v3.data_path.slice(5));
        return framev2;
    }
    static isValid(payload) {
        const schema = [
            "uid",
            "timestamp",
            "type",
            "sequence_uid",
            "campaign_uid",
            "campaign_name",
            "campaign_type",
            "route_uid",
            "height_above_ground",
            "direction",
            "play_reverted",
            "properties",
            // position & orientation
            "longitude",
            "latitude",
            "height",
            "epsg_height",
            "omega",
            "phi",
            "kappa",
            "prec_longitude",
            "prec_latitude",
            "prec_height",
            "prec_omega",
            "prec_phi",
            "prec_kappa",
            // image & dispmap meta
            "data_path",
            "multi_head_meta",
            "multi_head_mask",
            "im_type",
            "im_type_name",
            "im_tile_size",
            "im_width",
            "im_height",
            "im_focal_length",
            "im_pixel_size",
            "dm_reduction_factor",
            "dm_basis",
            "dm_num_disparities",
        ];
        return isValidSchema(payload, schema);
    }
}
//# sourceMappingURL=frame.js.map